import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ApiService } from '../services/api';
import { useModal } from '../hooks/useModal';

export default function VerifyPhonePage() {
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [resendCount, setResendCount] = useState(0);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const navigate = useNavigate();
    const location = useLocation();
    const { systemConfig, loginWithToken } = useAuth();
    const { showAlert } = useModal();

    const userId = location.state?.userId;
    const maskedPhone = location.state?.phone;
    const fromLogin = location.state?.fromLogin;

    useEffect(() => {
        if (!userId) {
            navigate('/login');
        }
    }, [userId, navigate]);

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    useEffect(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleCodeChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value.slice(-1);
        setCode(newCode);

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        if (value && index === 5 && newCode.every(d => d !== "")) {
            handleVerify(newCode.join(""));
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === "Backspace" && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        if (pastedData.length === 6) {
            const newCode = pastedData.split("");
            setCode(newCode);
            inputRefs.current[5]?.focus();
            handleVerify(pastedData);
        }
    };

    const handleVerify = async (verificationCode?: string) => {
        const codeStr = verificationCode || code.join("");
        if (codeStr.length !== 6) {
            setError("Lütfen 6 haneli doğrulama kodunu giriniz.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const result = await ApiService.verifyPhone(userId, codeStr);

            if (result?.autoLogin && result.token && result.user) {
                loginWithToken(result.token, result.refreshToken ?? '', result.user);

                await showAlert({
                    title: "Doğrulama Başarılı!",
                    message: "Telefon numaranız doğrulandı. Hoş geldiniz!",
                    type: "success",
                    buttonText: "Devam Et",
                });
                navigate("/");
            } else {
                await showAlert({
                    title: "Doğrulama Başarılı!",
                    message: "Telefon numaranız başarıyla doğrulandı. Artık giriş yapabilirsiniz.",
                    type: "success",
                    buttonText: "Giriş Yap",
                });
                navigate("/login");
            };
        } catch (err: any) {
            const errorMessage = err?.message || "Doğrulama sırasında bir hata oluştu.";
            setError(errorMessage);
            setCode(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (countdown > 0 || resendCount >= 3) return;

        setResending(true);
        setError("");

        try {
            await ApiService.resendPhoneCode(userId);
            setResendCount(prev => prev + 1);
            setCountdown(300);
            setCode(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
            await showAlert({
                title: "Kod Gönderildi 📱",
                message: "Yeni doğrulama kodu telefonunuza SMS olarak gönderildi.",
                type: "success",
                buttonText: "Tamam",
            });
        } catch (err: any) {
            const errorMessage = err?.message || "SMS gönderiminde bir hata oluştu.";
            setError(errorMessage);
        } finally {
            setResending(false);
        }
    };

    const formatCountdown = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    const renderError = (message: string) => {
        const timeRegex = /(\d+\s*dakika\s*\d+\s*saniye|\d+\s*saniye|\d+\/gün)/g;

        const match = message.match(timeRegex);
        if (!match) return message;

        const parts = message.split(timeRegex);

        return (
            <>
                {parts.map((part, i) =>
                    timeRegex.test(part) ? <strong key={i} className="font-bold">{part}</strong> : part
                )}
            </>
        );
    };

    if (!userId) return null;

    return (
        <div className="min-h-screen bg-white flex items-center justify-center px-4">
            <div className="w-full max-w-md">

                <div className="text-center mb-8">
                    <div className="mb-6 flex justify-center">
                        <img
                            src={systemConfig?.companyLogo || "/logo.svg"}
                            alt={systemConfig?.companyName || "Logo"}
                            className="h-24 w-24 object-cover rounded-full shadow-lg"
                        />
                    </div>

                    <div className="flex items-center justify-center mb-2">
                        <div className="text-3xl font-black tracking-tight text-gray-900">
                            {systemConfig?.companyName ? (
                                <span>{systemConfig.companyName}</span>
                            ) : (
                                <>
                                    <span>YES.</span><span className="text-purple-600">Tools</span>
                                </>
                            )}
                        </div>
                    </div>

                    <h2 className="text-lg font-medium text-gray-500 mb-4">
                        Telefon Doğrulama
                    </h2>

                    <p className="text-gray-400 text-sm max-w-xs mx-auto leading-relaxed">
                        {maskedPhone ? (
                            <>
                                <span className="font-semibold text-gray-600">{maskedPhone}</span> numaralı telefonunuza gönderilen 6 haneli doğrulama kodunu giriniz.
                            </>
                        ) : (
                            "Telefonunuza gönderilen 6 haneli doğrulama kodunu giriniz."
                        )}
                    </p>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg shadow-sm">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Doğrulama Hatası</h3>
                                <p className="mt-1 text-sm text-red-700">{renderError(error)}</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-center gap-3 mb-6">
                    {code.map((digit, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleCodeChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={index === 0 ? handlePaste : undefined}
                            disabled={loading}
                            className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all disabled:opacity-50 ${digit ? "border-purple-400 bg-purple-50 text-purple-700" : "border-gray-200"
                                }`}
                        />
                    ))}
                </div>

                <button
                    onClick={() => handleVerify()}
                    disabled={loading || code.some(d => d === "")}
                    className="w-full bg-purple-600 text-white py-3 px-4 rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all shadow-lg shadow-purple-600/20 font-semibold disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                >
                    {loading ? (
                        <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            Doğrulanıyor...
                        </div>
                    ) : (
                        "Doğrula"
                    )}
                </button>

                <div className="text-center space-y-3">
                    <p className="text-sm text-gray-500">
                        Kodu almadınız mı?
                    </p>

                    {resendCount >= 3 ? (
                        <p className="text-sm text-red-500 font-medium">
                            Günlük SMS gönderim limitinize ulaştınız (3/gün).
                        </p>
                    ) : countdown > 0 ? (
                        <p className="text-sm text-gray-400">
                            Yeni kod göndermek için{" "}
                            <span className="font-bold text-purple-600">{formatCountdown(countdown)}</span>{" "}
                            bekleyiniz.
                        </p>
                    ) : (
                        <button
                            onClick={handleResend}
                            disabled={resending}
                            className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium text-sm transition-colors disabled:opacity-50"
                        >
                            {resending ? (
                                <>
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Gönderiliyor...
                                </>
                            ) : (
                                <>
                                    <RefreshCw className="w-4 h-4" />
                                    Tekrar Gönder
                                </>
                            )}
                        </button>
                    )}

                    <div className="text-xs text-gray-400">
                        Kalan SMS hakkı: {3 - resendCount}/3
                    </div>
                </div>

                <div className="mt-8 text-center">
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-gray-500 hover:text-purple-600 text-sm font-medium transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Giriş Sayfasına Dön
                    </Link>
                </div>

                <div className="mt-8 text-center text-sm text-gray-500">
                    <Link to="#" className="hover:text-purple-600 transition-colors">
                        Kullanım Şartları
                    </Link>
                    <span className="mx-2">|</span>
                    <Link to="#" className="hover:text-purple-600 transition-colors">
                        Gizlilik Politikası
                    </Link>
                </div>
            </div>
        </div>
    );
}